import { useMutation } from '@apollo/client';
import { ButtonDock, miniButtonDockScrollBottomPad } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Button, List, Placeholder, Section, Text } from '@telegram-apps/telegram-ui';
import { hapticFeedback } from '@tma.js/sdk';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GoalPlan } from '../client/generated/graphql';
import { updateGoalPlansMutation } from '../client/mutations/updateGoalPlans';
import { ListCategory } from '../client/types';
import { CategoryIcon } from '../components/common/CategoryIcon';
import { CustomCell } from '../components/common/CustomCell';
import { FocusLeePlaceholderImage } from '../components/common/FocusLeePlaceholderImage';
import { GoalsSelectPageSkeleton } from '../components/goals/skeleton/GoalsSelectPageSkeleton';
import { Page } from '../components/Page';
import { getColor } from '../helpers/color';
import { getSpecificLevelTargetXP } from '../helpers/goal';
import { mapTargetPlanToNumber, markNames } from '../helpers/goals';
import { getButtonThemeColors } from '../helpers/theme';
import { useCachedCategoriesWithFallback } from '../hooks/caching/category';
import { useCachedGoalsWithFallback } from '../hooks/caching/goal';
import { categoryCreatePath } from '../navigation/paths';

const planDescriptions: Record<GoalPlan, string> = {
  [GoalPlan.WorkdaysOnly]: 'Mon-Fri focus',
  [GoalPlan.WorkdaysWithExtraEffort]: 'Extra on weekdays',
  [GoalPlan.ConsistentDailyEffort]: 'Every day',
  [GoalPlan.WeekendBoost]: 'Extra on weekends',
  [GoalPlan.WeekendOnly]: 'Sat-Sun focus',
};

const planEmojis: Record<GoalPlan, string> = {
  [GoalPlan.WorkdaysOnly]: '💼',
  [GoalPlan.WorkdaysWithExtraEffort]: '💪',
  [GoalPlan.ConsistentDailyEffort]: '🔥',
  [GoalPlan.WeekendBoost]: '🚀',
  [GoalPlan.WeekendOnly]: '🎉',
};

export const GoalsSelectPage: FC = () => {
  const { goals: allGoals, loading: goalsLoading } = useCachedGoalsWithFallback();
  const { categories, loading: categoriesLoading } = useCachedCategoriesWithFallback();
  const [updateGoalPlans, { loading: saving }] = useMutation(updateGoalPlansMutation);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const byPass = searchParams.get('byPass');

  const loading = goalsLoading || categoriesLoading;

  const [selected, setSelected] = useState<string[]>([]);
  const [levelMap, setLevelMap] = useState<Record<string, number>>({});
  const [planMap, setPlanMap] = useState<Record<string, GoalPlan>>({});
  const planList: GoalPlan[] = [
    GoalPlan.WorkdaysOnly,
    GoalPlan.WeekendOnly,
    GoalPlan.ConsistentDailyEffort,
    GoalPlan.WeekendBoost,
    GoalPlan.WorkdaysWithExtraEffort,
  ];

  useEffect(() => {
    if (allGoals.length > 0) {
      const enabledGoals = allGoals.filter(g => g.enabled);
      const initial = enabledGoals.filter(g => g.active).map(g => g.id);
      setSelected(initial);
      setLevelMap(Object.fromEntries(enabledGoals.map(g => [g.id, g.targetLevel ?? 1])));
      setPlanMap(Object.fromEntries(enabledGoals.map(g => [g.id, g.targetPlan ?? planList[2]])));
    }
  }, [allGoals]);

  const categoryMap: Record<string, ListCategory> = Object.fromEntries(categories.map(c => [c.id, c]));
  const enabledGoals = useMemo(() => allGoals.filter(g => g.enabled), [allGoals]);
  const sortedGoals = [
    ...enabledGoals.filter(g => selected.includes(g.id)),
    ...enabledGoals.filter(g => !selected.includes(g.id)),
  ];

  const handleGoalToggle = useCallback((goalId: string) => {
    hapticFeedback.impactOccurred.ifAvailable('light');
    if (selected.includes(goalId)) {
      setSelected(selected.filter(id => id !== goalId));
    } else if (selected.length < 3) {
      setSelected([...selected, goalId]);
    }
  }, [selected]);

  const handleSave = useCallback(async () => {
    try {
      await updateGoalPlans({
        variables: {
          goalIdsToActivate: selected,
          goalIdsToDeactivate: enabledGoals.filter(g => !selected.includes(g.id)).map(g => g.id),
          goalPlans: enabledGoals.map(g => ({
            id: g.id,
            targetLevel: levelMap[g.id],
            targetPlan: planMap[g.id],
          })),
        },
      });
      navigate(byPass ? -3: -2);
    } catch (error) {
      console.error(error);
    }
  }, [selected, enabledGoals, levelMap, planMap, byPass]);

  const handleCancel = useCallback(() => {
    navigate(byPass ? -3: -2);
  }, [navigate]);

  const isValid = sortedGoals.length > 0 && selected.length > 0 && selected.length <= 3 && !loading && !saving;

  if (loading) return <GoalsSelectPageSkeleton />;

  return (
    <Page
      back
      insetEnabled={false}
      backClick={handleCancel}
      footer={
        <ButtonDock
          secondary={{ text: 'Back', onClick: handleCancel, navigation: 'back' }}
          main={{
            text: 'Save',
            onClick: handleSave,
            enabled: isValid,
            loading: saving,
            bgColor: getButtonThemeColors(isValid).backgroundColor,
          }}
        />
      }
    >
      <Stack alignItems="center" justifyContent="center" minHeight="var(--tg-viewport-content-safe-area-inset-top)">
        <Text style={{ fontSize: '18px', textAlign: 'center' }}>Edit Goals</Text>
      </Stack>
      {sortedGoals.length === 0 ? (
        <Stack direction="column" justifyContent="center" alignItems="center" height="100%" gap={2}>
          <Placeholder
            header="No goals available"
            description="Create a category to get started"
            style={{ padding: 0 }}
          >
            <FocusLeePlaceholderImage kind="goals" />
          </Placeholder>
          <Button
            mode="filled"
            onClick={() => navigate(`${categoryCreatePath}?fromGoals=true`)}
          >
            Create Category
          </Button>
        </Stack>
      ) : (
        <List
          style={{
            background: 'var(--tgui--secondary_bg_color)',
            padding: 10,
            paddingBottom: miniButtonDockScrollBottomPad,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Box sx={{ padding: '0 16px', marginBottom: '8px' }}>
            <Text style={{ fontSize: '14px', color: 'var(--tgui--hint_color)' }}>
              You can select up to 3 goals
            </Text>
          </Box>
          {sortedGoals.map(g => { 
            const cat = categoryMap[g.categoryId];
            const isSelected = selected.includes(g.id);
            const currentLevel = levelMap[g.id] ?? 1;
            const currentPlan = planMap[g.id] ?? planList[2];
            const currentLevelXP = getSpecificLevelTargetXP(g, currentLevel);
            
            return (
                  <Section key={g.id} style={{ 
                      padding: '0 4px', 
                      marginBottom: '8px',
                    }}>
                      <CustomCell 
                        onClick={() => {
                          handleGoalToggle(g.id);
                        }}
                        disabled={!isSelected && selected.length >= 3}
                        style={{
                          border: isSelected ? '2px solid var(--tg-theme-button-color)' : '2px solid transparent',
                          borderRadius: '12px',
                          transition: 'all 0.5s ease',
                          opacity: !isSelected && selected.length >= 3 ? 0.5 : 1,
                        }}
                      >
                        <Stack direction="row" spacing={1} mb={isSelected ? '16px' : '0px'} alignItems="center">
                          <Box sx={{
                            width: 20,
                            height: 20,
                            borderRadius: 1,
                            background: getColor(cat?.iconColor ?? ''),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <CategoryIcon
                              icon={cat?.icon || 'emoji_events'}
                              iconColor={cat?.iconColor || ''}
                              customIcon={cat?.customIcon}
                              type="filled"
                              size={14}
                              color="var(--tg-theme-text-color)"
                            />
                          </Box>
                          <Text>{g.name}</Text>
                        </Stack>
                        {isSelected && (
                          <Box>
                            {/* Target Level Section */}
                            <Box mb={3}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                <Text style={{ fontSize: '14px', fontWeight: 500, color: 'var(--tgui--text_color)' }}>
                                  Target Level
                                </Text>
                                <Text style={{ fontSize: '14px', color: 'var(--tg-theme-button-color)' }}>
                                  {currentLevelXP} XP
                                </Text>
                              </Stack>
                              <Stack direction="row" spacing={1}>
                                {[1, 2, 3].map(lv => {
                                  const isLevelSelected = currentLevel === lv;
                                  return (
                                    <Box
                                      key={lv}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setLevelMap(prev => ({ ...prev, [g.id]: lv }));
                                      }}
                                      sx={{
                                        flex: 1,
                                        padding: '10px 8px',
                                        borderRadius: '8px',
                                        background: isLevelSelected 
                                          ? 'var(--tg-theme-button-color)' 
                                          : 'var(--tgui--secondary_bg_color)',
                                        color: isLevelSelected 
                                          ? 'var(--tg-theme-button-text-color)' 
                                          : 'var(--tgui--text_color)',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.5s ease',
                                        border: isLevelSelected 
                                          ? 'none' 
                                          : '1px solid transparent',
                                      }}
                                    >
                                      <Text style={{ fontSize: '14px', fontWeight: isLevelSelected ? 600 : 400 }}>
                                        {lv}
                                      </Text>
                                    </Box>
                                  );
                                })}
                              </Stack>
                            </Box>

                            {/* Target Plan Section */}
                            <Box>
                              <Text style={{ fontSize: '14px', fontWeight: 500, color: 'var(--tgui--text_color)', marginBottom: '8px', display: 'block' }}>
                                Target Plan
                              </Text>
                              <Stack spacing={1}>
                                {planList.map(plan => {
                                  const isPlanSelected = currentPlan === plan;
                                  const planIndex = mapTargetPlanToNumber(plan);
                                  return (
                                    <Box
                                      key={plan}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setPlanMap(prev => ({ ...prev, [g.id]: plan }));
                                      }}
                                      sx={{
                                        padding: '12px 16px',
                                        borderRadius: '12px',
                                        background: isPlanSelected 
                                          ? 'var(--tg-theme-button-color)' 
                                          : 'var(--tgui--secondary_bg_color)',
                                        cursor: 'pointer',
                                        transition: 'all 0.5s ease',
                                        border: isPlanSelected 
                                          ? 'none' 
                                          : '1px solid var(--tgui--separator_color)',
                                      }}
                                    >
                                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                        <Text style={{ fontSize: '20px', lineHeight: '20px', marginTop: '2px' }}>{planEmojis[plan]}</Text>
                                        <Stack direction="column">
                                          <Text style={{ 
                                            fontSize: '15px', 
                                            fontWeight: isPlanSelected ? 500 : 400,
                                            color: isPlanSelected 
                                              ? 'var(--tg-theme-button-text-color)' 
                                              : 'var(--tgui--text_color)',
                                            marginBottom: '2px',
                                            lineHeight: '18px',
                                          }}>
                                            {markNames[planIndex - 1]}
                                          </Text>
                                          <Text style={{ 
                                            fontSize: '13px',
                                            color: isPlanSelected 
                                              ? 'rgba(255, 255, 255, 0.8)' 
                                              : 'var(--tgui--hint_color)',
                                            lineHeight: '16px',
                                            whiteSpace: 'normal',
                                            wordWrap: 'break-word',
                                          }}>
                                            {planDescriptions[plan]}
                                          </Text>
                                        </Stack>
                                      </Stack>
                                    </Box>
                                  );
                                })}
                              </Stack>
                            </Box>
                          </Box>
                        )}
                      </CustomCell>
                  </Section>
            )})}
      </List>
      )}
    </Page>
  );
};
